import React, { useEffect, useState } from 'react';
import PriceHistoryChart from './PriceHistoryChart';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function normalize(str) {
    return (str || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');
}

function levenshtein(a, b) {
    const an = a.length;
    const bn = b.length;
    if (an === 0) return bn;
    if (bn === 0) return an;
    const matrix = Array.from({ length: an + 1 }, (_, i) => [i]);
    for (let j = 0; j <= bn; j++) matrix[0][j] = j;
    for (let i = 1; i <= an; i++) {
        for (let j = 1; j <= bn; j++) {
            matrix[i][j] = Math.min(
                matrix[i - 1][j] + 1,
                matrix[i][j - 1] + 1,
                matrix[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
            );
        }
    }
    return matrix[an][bn];
}

function ProductDetail({ productId }) {
    const [data, setData] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [metricsLoading, setMetricsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);

    // URLs asociadas
    const [productUrls, setProductUrls] = useState([]);
    const [urlsLoading, setUrlsLoading] = useState(false);

    // Enlazar producto individual
    const [enlazarIdx, setEnlazarIdx] = useState(null);
    const [newUrl, setNewUrl] = useState('');
    const [urlError, setUrlError] = useState('');
    const [urlSubmitting, setUrlSubmitting] = useState(false);

    useEffect(() => {
        setLoading(true);
        setError(null);
        fetch(`${BASE_URL}/api/products/${productId}`)
            .then(res => res.json())
            .then(setData)
            .catch(e => setError('Error al cargar detalles'))
            .finally(() => setLoading(false));
    }, [productId]);

    useEffect(() => {
        setMetricsLoading(true);
        fetch(`${BASE_URL}/api/products/${productId}/metrics`)
            .then(res => res.json())
            .then(setMetrics)
            .catch(() => setMetrics(null))
            .finally(() => setMetricsLoading(false));
    }, [productId]);

    useEffect(() => {
        setUrlsLoading(true);
        fetch(`${BASE_URL}/api/products/${productId}/urls`)
            .then(res => {
                if (!res.ok) throw new Error();
                return res.json();
            })
            .then(setProductUrls)
            .catch(() => setProductUrls([]))
            .finally(() => setUrlsLoading(false));
    }, [productId]);

    const handleCreateUrl = async (url) => {
        setUrlError('');
        if (!url || !/^https?:\/\/.+/.test(url)) {
            setUrlError('Ingresa una URL válida (debe comenzar con http o https)');
            return;
        }
        setUrlSubmitting(true);
        try {
            const res = await fetch(`${BASE_URL}/api/products/${productId}/urls`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ product_url: url })
            });
            if (res.status === 201) {
                const created = await res.json();
                setProductUrls(prev => [created, ...prev]);
                setEnlazarIdx(null);
                setNewUrl('');
            } else if (res.status === 400) {
                setUrlError('Faltan datos requeridos o formato inválido');
            } else {
                setUrlError('Error al crear la URL');
            }
        } catch {
            setUrlError('Error de red al crear la URL');
        }
        setUrlSubmitting(false);
    };

    const handleDeleteUrl = async (id) => {
        if (!window.confirm('¿Eliminar este enlace?')) return;
        try {
            const res = await fetch(`${BASE_URL}/api/product-urls/${id}`, { method: 'DELETE' });
            if (res.status === 204) {
                setProductUrls(prev => prev.filter(u => u.id !== id));
            }
        } catch {
            // Silenciar error
        }
    };

    if (loading) return <div style={{ padding: 32, fontSize: 22, textAlign: 'center' }}>Cargando detalles...</div>;
    if (error || !data) return <div style={{ color: 'red', padding: 32, fontSize: 20 }}>{error || 'No hay datos.'}</div>;

    const { product, price_history, latest_prices } = data;

    // Imagen principal: mejor coincidencia entre product.handle y product_title
    const normHandle = normalize(product.handle);
    let bestScore = Infinity;
    let bestImage = product.img || null;
    if (price_history && price_history.length > 0) {
        price_history.forEach(row => {
            const normTitle = normalize(row.product_title);
            const score = levenshtein(normHandle, normTitle);
            if (score < bestScore && row.product_image) {
                bestScore = score;
                bestImage = row.product_image;
            }
        });
    }
    const galleryImages = [
        ...(product.images || []).map(url =>
            url.startsWith('http') ? url : (url.startsWith('//') ? 'https:' + url : url)
        ),
        ...(bestImage ? [bestImage] : []),
    ].filter((v, i, arr) => arr.indexOf(v) === i);

    const mainImage = selectedImage || (galleryImages.length > 0 ? galleryImages[0] : null);

    // Ordenar price_history por similitud y fecha descendente
    const sortedHistory = [...(price_history || [])].sort((a, b) => {
        const simA = levenshtein(normHandle, normalize(a.product_title));
        const simB = levenshtein(normHandle, normalize(b.product_title));
        if (simA !== simB) return simA - simB;
        return (b.checked_at || '').localeCompare(a.checked_at || '');
    });

    // Precios actuales por tienda (usando links asociados)
    const currentStoreLinks = (
        <div style={{ marginBottom: 24 }}>
            <h3 style={{ margin: '8px 0 16px 0' }}>Precios actuales por tienda</h3>
            {urlsLoading ? (
                <div>Cargando links...</div>
            ) : productUrls.length === 0 ? (
                <div style={{ color: '#888', fontSize: 15 }}>No hay links asociados.</div>
            ) : (
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                    {productUrls.map(url => (
                        <div key={url.id} style={{
                            flex: 1,
                            minWidth: '100%',
                            maxWidth: 440,
                            background: '#fff',
                            borderRadius: 10,
                            boxShadow: '0 2px 8px #e0e0e0',
                            padding: 18,
                            margin: 4,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 18,
                            border: '1px solid #f0f0f0',
                            position: 'relative'
                        }}>
                            {url.product_image && (
                                <img
                                    src={url.product_image}
                                    alt={url.product_title}
                                    style={{
                                        width: 62,
                                        height: 62,
                                        objectFit: 'contain',
                                        borderRadius: 8,
                                        background: '#fafafa',
                                        border: '1px solid #eee',
                                        marginRight: 8
                                    }}
                                    onError={e => { e.target.style.display = 'none'; }}
                                />
                            )}
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 2 }}>
                                    <a
                                        href={url.product_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#1976d2', textDecoration: 'underline', wordBreak: 'break-all' }}
                                    >
                                        {url.product_title || url.product_url}
                                        <span style={{ fontSize: 15, marginLeft: 4, verticalAlign: 'middle' }}>🔗</span>
                                    </a>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 4 }}>
                                    {url.store_name && (
                                        <span style={{ fontSize: 14, color: '#444', fontWeight: 500 }}>
                                            {url.store_name}
                                        </span>
                                    )}
                                    {url.store_merchant && (
                                        <span style={{ fontSize: 13, color: '#888' }}>
                                            Seller: {url.store_merchant}
                                        </span>
                                    )}
                                    {url.product_category && (
                                        <span style={{ fontSize: 13, color: '#888' }}>
                                            {url.product_category}
                                        </span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                                    <span style={{ fontSize: 18, color: '#1976d2', fontWeight: 700 }}>
                                        {url.store_price ? `$${url.store_price.toLocaleString()}` : '-'}
                                    </span>
                                    {url.product_discount && (
                                        <span style={{
                                            background: '#ffecb3',
                                            color: '#bf360c',
                                            borderRadius: 5,
                                            padding: '2px 8px',
                                            fontSize: 13,
                                            fontWeight: 600,
                                            marginLeft: 2
                                        }}>
                                            {url.product_discount}% OFF
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: 13, color: '#888', marginTop: 8 }}>
                                    {url.product_description}
                                </div>
                                <div style={{ fontSize: 13, color: '#888', marginTop: 6 }}>
                                    Creado: {url.created_at?.replace('T', ' ').slice(0, 19)}<br />
                                    Actualizado: {url.updated_at?.replace('T', ' ').slice(0, 19)}
                                </div>
                            </div>
                            <button
                                style={{
                                    background: '#e53935',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 4,
                                    padding: '4px 10px',
                                    fontSize: 14,
                                    cursor: 'pointer',
                                    marginLeft: 'auto',
                                    alignSelf: 'flex-start'
                                }}
                                onClick={() => handleDeleteUrl(url.id)}
                                title="Eliminar enlace"
                            >
                                Eliminar
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // Productos Similares (antes era precios actuales por tienda)
    const priceCards = (latest_prices || []).map((lp, idx) => (
        <div key={lp.store_name} style={{
            flex: 1,
            minWidth: 220,
            background: '#fff',
            borderRadius: 10,
            boxShadow: '0 2px 8px #e0e0e0',
            padding: 16,
            margin: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            border: '1px solid #f0f0f0',
            position: 'relative'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {lp.product_image && (
                    <img src={lp.product_image} alt={lp.product_title} style={{
                        width: 48, height: 48, objectFit: 'contain', borderRadius: 8, background: '#fafafa', border: '1px solid #eee', marginRight: 10
                    }} />
                )}
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>
                        {lp.product_url ? (
                            <a
                                href={lp.product_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', textDecoration: 'underline' }}
                            >
                                {lp.store_name}
                                <span style={{ fontSize: 15, marginLeft: 4, verticalAlign: 'middle' }}>🔗</span>
                            </a>
                        ) : (
                            lp.store_name
                        )}
                    </div>
                    <div style={{ fontSize: 18, color: '#1976d2', fontWeight: 700 }}>
                        {lp.store_price ? `$${lp.store_price.toLocaleString()}` : '-'}
                    </div>
                    <div style={{ fontSize: 13, color: '#888', fontWeight: 700 }}>
                        {lp.product_title ? `${lp.product_title}` : null}
                    </div>
                    <div style={{ fontSize: 13, color: '#888' }}>
                        {lp.product_discount ? `Descuento: ${lp.product_discount}%` : null}
                    </div>
                    <div style={{ fontSize: 13, color: '#888' }}>
                        {lp.product_category}
                    </div>
                </div>
            </div>
            <button
                style={{
                    background: '#1976d2',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 5,
                    padding: '5px 12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: 14,
                    marginTop: 14,
                    alignSelf: 'flex-end'
                }}
                onClick={() => {
                    setEnlazarIdx(idx);
                    setNewUrl(lp.product_url || '');
                    setUrlError('');
                }}
                disabled={urlSubmitting}
            >
                Enlazar producto
            </button>
            {enlazarIdx === idx && (
                <div style={{
                    marginTop: 10,
                    background: '#fffde7',
                    border: '1px solid #ffe082',
                    borderRadius: 6,
                    padding: 10,
                    width: '100%',
                    zIndex: 2
                }}>
                    <div style={{ fontSize: 14, marginBottom: 6 }}>
                        ¿Enlazar este producto? Confirma o edita la URL:
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                            type="url"
                            value={newUrl}
                            onChange={e => setNewUrl(e.target.value)}
                            placeholder="https://competidor.com/producto/xyz"
                            style={{
                                flex: 1,
                                padding: 7,
                                borderRadius: 4,
                                border: '1px solid #bdbdbd',
                                fontSize: 14
                            }}
                            required
                            autoFocus
                            disabled={urlSubmitting}
                        />
                        <button
                            style={{
                                background: '#388e3c',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 4,
                                padding: '6px 10px',
                                fontWeight: 600,
                                fontSize: 14,
                                cursor: 'pointer'
                            }}
                            onClick={() => handleCreateUrl(newUrl)}
                            disabled={urlSubmitting}
                        >
                            {urlSubmitting ? 'Guardando...' : 'Confirmar'}
                        </button>
                        <button
                            style={{
                                background: '#eee',
                                color: '#444',
                                border: 'none',
                                borderRadius: 4,
                                padding: '6px 8px',
                                fontSize: 14,
                                cursor: 'pointer'
                            }}
                            onClick={() => { setEnlazarIdx(null); setUrlError(''); setNewUrl(''); }}
                            disabled={urlSubmitting}
                        >
                            Cancelar
                        </button>
                    </div>
                    {urlError && <div style={{ color: 'red', marginTop: 6 }}>{urlError}</div>}
                </div>
            )}
        </div>
    ));

    const statCards = metrics && !metrics.error && (
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ background: '#e3f2fd', borderRadius: 8, padding: 18, minWidth: 120, fontSize: 16 }}>
                <b>Precio mínimo:</b><br />${metrics.min?.toLocaleString()}
            </div>
            <div style={{ background: '#fffde7', borderRadius: 8, padding: 18, minWidth: 120, fontSize: 16 }}>
                <b>Precio máximo:</b><br />${metrics.max?.toLocaleString()}
            </div>
            <div style={{ background: '#e8f5e9', borderRadius: 8, padding: 18, minWidth: 120, fontSize: 16 }}>
                <b>Promedio:</b><br />${metrics.avg?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
            <div style={{ background: '#fce4ec', borderRadius: 8, padding: 18, minWidth: 120, fontSize: 16 }}>
                <b>Volatilidad:</b><br />{metrics.volatility?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </div>
        </div>
    );

    const infoRows = [
        { label: 'SKU', value: product.sku },
        { label: 'ID', value: product.id },
        { label: 'Marca', value: product.brand },
        { label: 'Tipo', value: product.product_type },
        { label: 'Disponibilidad', value: product.availability },
        { label: 'Precio actual', value: product.price ? `$${product.price.toLocaleString()}` : '-' },
        { label: 'Precio referencia', value: product.compare_price ? `$${product.compare_price.toLocaleString()}` : '-' },
    ];

    return (
        <div style={{
            maxWidth: 1100,
            margin: '0 auto',
            padding: 24,
            fontFamily: 'Segoe UI,Roboto,Arial,sans-serif'
        }}>
            {/* Título y galería */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 36,
                marginBottom: 18,
                alignItems: 'flex-start'
            }}>
                <div style={{ flex: '0 0 auto', maxWidth: 340 }}>
                    {mainImage && (
                        <div style={{ textAlign: 'center', marginBottom: 12 }}>
                            <img
                                src={mainImage}
                                alt={product.handle}
                                style={{
                                    width: 240,
                                    height: 240,
                                    objectFit: 'contain',
                                    borderRadius: 14,
                                    background: '#fff',
                                    border: '2px solid #e0e0e0',
                                    boxShadow: '0 4px 24px #e0e0e0'
                                }}
                                onError={e => { e.target.style.display = 'none'; }}
                            />
                        </div>
                    )}
                    {galleryImages.length > 1 && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                            {galleryImages.map((img, idx) => (
                                <img
                                    key={img + idx}
                                    src={img}
                                    alt={`galería ${idx}`}
                                    style={{
                                        width: 48,
                                        height: 48,
                                        objectFit: 'contain',
                                        borderRadius: 6,
                                        border: selectedImage === img ? '2px solid #1976d2' : '1px solid #eee',
                                        cursor: 'pointer',
                                        background: '#fff',
                                        boxShadow: selectedImage === img ? '0 2px 8px #1976d233' : undefined
                                    }}
                                    onClick={() => setSelectedImage(img)}
                                    onError={e => { e.target.style.display = 'none'; }}
                                />
                            ))}
                        </div>
                    )}
                </div>
                <div style={{ flex: 1, minWidth: 260 }}>
                    <h2 style={{ fontSize: 28, margin: '0 0 12px 0', color: '#222', lineHeight: 1.15 }}>
                        {product.canonical_url ? (
                            <a
                                href={product.canonical_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', textDecoration: 'none' }}
                            >
                                {product.title || product.handle}
                                <span style={{ fontSize: 18, marginLeft: 6, verticalAlign: 'middle' }}>🔗</span>
                            </a>
                        ) : (
                            product.title || product.handle
                        )}
                    </h2>
                    <div style={{ color: '#666', fontSize: 16, marginBottom: 8 }}>{product.description}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 8 }}>
                        {infoRows.map(row => (
                            <div key={row.label} style={{ minWidth: 110 }}>
                                <span style={{ color: '#888', fontSize: 13 }}>{row.label}:</span>
                                <span style={{ fontWeight: 600, marginLeft: 6 }}>{row.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Precios actuales por tienda (usando links asociados) */}
            {currentStoreLinks}

            {/* Productos Similares */}
            <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                    <h3 style={{ margin: '8px 0 16px 0' }}>Productos Similares</h3>
                </div>
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                    {priceCards.length > 0 ? priceCards : <div style={{ color: '#888' }}>Sin productos similares.</div>}
                </div>
            </div>

            {/* Estadísticas */}
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ margin: '8px 0 16px 0' }}>Estadísticas de precios</h3>
                {metricsLoading ? (
                    <div>Cargando métricas...</div>
                ) : statCards ? statCards : <div style={{ color: 'red' }}>No hay métricas disponibles.</div>}
            </div>

            {/* Gráfico */}
            <PriceHistoryChart history={price_history} />

            {/* Historial de precios */}
            <div style={{ marginTop: 36 }}>
                <h3 style={{ margin: '8px 0 16px 0' }}>Historial de precios (últimos 20 registros)</h3>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{
                        width: '100%',
                        fontSize: 15,
                        background: '#fff',
                        borderRadius: 8,
                        boxShadow: '0 1px 4px #eee',
                        borderCollapse: 'collapse',
                        minWidth: 800
                    }}>
                        <thead>
                        <tr style={{ background: '#f0f0f0' }}>
                            <th style={{ padding: 7 }}>Imagen</th>
                            <th style={{ padding: 7 }}>Fecha</th>
                            <th style={{ padding: 7 }}>Seller</th>
                            <th style={{ padding: 7 }}>Tienda</th>
                            <th style={{ padding: 7 }}>Precio</th>
                            <th style={{ padding: 7 }}>Título</th>
                            <th style={{ padding: 7 }}>Descuento</th>
                            <th style={{ padding: 7 }}>Categoría</th>
                        </tr>
                        </thead>
                        <tbody>
                        {sortedHistory.slice(0, 20).map((row, idx) => (
                            <tr key={row.id || idx} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                <td style={{ padding: 7 }}>
                                    {row.product_image && (
                                        <img
                                            src={row.product_image}
                                            alt={row.product_title}
                                            style={{ width: 38, height: 38, objectFit: 'contain', borderRadius: 4, border: '1px solid #eee', background: '#fafafa' }}
                                            onError={e => { e.target.style.display = 'none'; }}
                                        />
                                    )}
                                </td>
                                <td style={{ padding: 7 }}>{row.checked_at?.replace('T', ' ').slice(0, 19)}</td>
                                <td style={{ padding: 7 }}>{row.store_merchant}</td>
                                <td style={{ padding: 7 }}>{row.store_name}</td>
                                <td style={{ padding: 7 }}>{row.store_price ? `$${row.store_price.toLocaleString()}` : '-'}</td>
                                <td style={{ padding: 7 }}>
                                    {row.product_url ? (
                                        <a
                                            href={row.product_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#1976d2', textDecoration: 'underline' }}
                                        >
                                            {row.product_title}
                                            <span style={{ fontSize: 14, marginLeft: 4, verticalAlign: 'middle' }}>🔗</span>
                                        </a>
                                    ) : (
                                        row.product_title
                                    )}
                                </td>
                                <td style={{ padding: 7 }}>{row.product_discount || '-'}</td>
                                <td style={{ padding: 7 }}>{row.product_category || '-'}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default ProductDetail;
